import { supabase } from '../lib/supabase';
import { Message } from '../types';

/**
 * Utility functions for message handling
 */

export interface MessageData {
  content: string;
  attachments?: File[];
  replyTo?: string;
}

/**
 * Send a new message
 */
export const sendMessage = async (
  senderId: string,
  recipientId: string,
  messageData: MessageData
): Promise<Message> => {
  try {
    // Check if Supabase is configured
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    if (!supabaseUrl || !supabaseKey || 
        supabaseUrl.includes('placeholder') || supabaseKey.includes('placeholder') ||
        supabaseUrl === 'your_supabase_project_url' || supabaseKey === 'your_supabase_anon_key') {
      // Demo mode - return mock message
      const mockMessage: Message = {
        id: Math.random().toString(36).substring(2, 15),
        senderId,
        recipientId,
        content: messageData.content,
        read: false,
        createdAt: new Date().toISOString()
      };
      return mockMessage;
    }

    // Upload attachments if any
    let attachmentUrls: string[] = [];
    if (messageData.attachments && messageData.attachments.length > 0) {
      attachmentUrls = await uploadMessageAttachments(messageData.attachments, senderId);
    }

    // Create message in database
    const { data, error } = await supabase
      .from('messages')
      .insert({
        sender_id: senderId,
        recipient_id: recipientId,
        content: messageData.content,
        attachments: attachmentUrls.length > 0 ? attachmentUrls : undefined
      })
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      senderId: data.sender_id,
      recipientId: data.recipient_id,
      content: data.content,
      read: data.read,
      createdAt: data.created_at
    };

  } catch (error) {
    console.error('Error sending message:', error);
    throw new Error('Error al enviar el mensaje');
  }
};

/**
 * Upload message attachments to storage
 */
export const uploadMessageAttachments = async (
  files: File[],
  userId: string
): Promise<string[]> => {
  const uploadedUrls: string[] = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const fileExt = file.name.split('.').pop();
    const fileName = `messages/${userId}/${Date.now()}-${i}.${fileExt}`;

    const { data, error } = await supabase.storage
      .from('attachments')
      .upload(fileName, file);

    if (error) {
      console.error('Error uploading attachment:', error);
      continue;
    }

    const { data: { publicUrl } } = supabase.storage
      .from('attachments')
      .getPublicUrl(fileName);

    uploadedUrls.push(publicUrl);
  }

  return uploadedUrls;
};

/**
 * Mark messages as read
 */
export const markMessagesAsRead = async (messageIds: string[]): Promise<void> => {
  try {
    const { error } = await supabase
      .from('messages')
      .update({ read: true })
      .in('id', messageIds);

    if (error) throw error;
  } catch (error) {
    console.error('Error marking messages as read:', error);
  }
};

/**
 * Get conversation between two users
 */
export const getConversation = async (
  userId1: string,
  userId2: string,
  limit: number = 50
): Promise<Message[]> => {
  try {
    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .or(`and(sender_id.eq.${userId1},recipient_id.eq.${userId2}),and(sender_id.eq.${userId2},recipient_id.eq.${userId1})`)
      .order('created_at', { ascending: true })
      .limit(limit);

    if (error) throw error;

    return data.map(msg => ({
      id: msg.id,
      senderId: msg.sender_id,
      recipientId: msg.recipient_id,
      content: msg.content,
      read: msg.read,
      createdAt: msg.created_at
    }));

  } catch (error) {
    console.error('Error fetching conversation:', error);
    return [];
  }
};

/**
 * Search messages
 */
export const searchMessages = async (
  userId: string,
  query: string
): Promise<Message[]> => {
  try {
    const { data, error } = await supabase
      .from('messages')
      .select('*')
      .or(`sender_id.eq.${userId},recipient_id.eq.${userId}`)
      .textSearch('content', query)
      .order('created_at', { ascending: false })
      .limit(20);

    if (error) throw error;

    return data.map(msg => ({
      id: msg.id,
      senderId: msg.sender_id,
      recipientId: msg.recipient_id,
      content: msg.content,
      read: msg.read,
      createdAt: msg.created_at
    }));

  } catch (error) {
    console.error('Error searching messages:', error);
    return [];
  }
};

/**
 * Delete a message
 */
export const deleteMessage = async (messageId: string, userId: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('messages')
      .delete()
      .eq('id', messageId)
      .eq('sender_id', userId); // Only sender can delete

    if (error) throw error;
    return true;

  } catch (error) {
    console.error('Error deleting message:', error);
    return false;
  }
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Validate message content
 */
export const validateMessage = (content: string, attachments?: File[]): { isValid: boolean; error?: string } => {
  if (!content.trim() && (!attachments || attachments.length === 0)) {
    return { isValid: false, error: 'El mensaje no puede estar vacío' };
  }

  if (content.length > 2000) {
    return { isValid: false, error: 'El mensaje es demasiado largo (máximo 2000 caracteres)' };
  }

  if (attachments && attachments.length > 5) {
    return { isValid: false, error: 'Máximo 5 archivos adjuntos permitidos' };
  }

  if (attachments) {
    for (const file of attachments) {
      if (file.size > 25 * 1024 * 1024) { // 25MB limit
        return { isValid: false, error: `El archivo ${file.name} es demasiado grande (máximo 25MB)` };
      }
    }
  }

  return { isValid: true };
};